import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi, Plant } from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { GridContainer, GridItem } from '../components/CustomGrid';
import SearchIcon from '@mui/icons-material/Search';

const PlantsSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Parse query params for search/category
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchTerm(params.get('search') || '');
    setFilterType(params.get('category') || 'all');
  }, [location.search]);

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true);
        const data = await api.getPlants();
        setPlants(data.filter(plant => plant.published));
      } catch (err) {
        setError('Failed to load plants');
      } finally {
        setLoading(false);
      }
    };
    fetchPlants();
  }, [api]);

  // Filter and search plants
  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (plant.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || plant.type === filterType;
    return matchesSearch && matchesType;
  });

  // Get unique plant types for filter
  const plantTypes = ['all', ...Array.from(new Set(plants.map(plant => plant.type)))];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/plants?search=${encodeURIComponent(searchTerm)}&category=${encodeURIComponent(filterType)}`);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Palm Encyclopedia
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Browse and search all palm species in our collection.
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search palms..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: '200px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: '150px' }}>
          <InputLabel id="filter-type-label">Category</InputLabel>
          <Select
            labelId="filter-type-label"
            value={filterType}
            label="Category"
            onChange={e => setFilterType(e.target.value)}
          >
            {plantTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type === 'all' ? 'All Categories' : type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained">Search</Button>
      </Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredPlants.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No palms found matching your search criteria.
        </Typography>
      ) : (
        <GridContainer spacing={3}>
          {filteredPlants.map(plant => (
            <GridItem xs={12} sm={6} md={4} key={plant.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {plant.imageUrl && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={plant.imageUrl}
                    alt={plant.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {plant.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {plant.type}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => navigate(`/plants/${plant.id}`)}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            </GridItem>
          ))}
        </GridContainer>
      )}
    </Box>
  );
};

export default PlantsSearchPage; 